/**
 * TanStack Query hooks for `aws-s3-data-export-integration`. Thin wrappers over ./api.
 */
import {
	useMutation,
	useQuery,
	useQueryClient,
	type UseMutationResult,
	type UseQueryResult,
} from "@tanstack/react-query";
import {
	awsS3DataExportIntegrationQueryKeys,
	createDestination,
	createJob,
	listDestinations,
	listJobs,
	type CreateDestinationBody,
	type CreateJobBody,
	type DestinationRow,
	type JobRow,
	type ListDestinationsResponse,
	type ListJobsResponse,
} from "./api";

export function useDestinationsList(): UseQueryResult<
	ListDestinationsResponse,
	Error
> {
	return useQuery({
		queryKey: awsS3DataExportIntegrationQueryKeys.destinations(),
		queryFn: listDestinations,
	});
}

export function useCreateDestination(): UseMutationResult<
	{ destination: DestinationRow },
	Error,
	CreateDestinationBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createDestination,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: awsS3DataExportIntegrationQueryKeys.destinations(),
			});
		},
	});
}

export function useJobsList(
	status?: string,
): UseQueryResult<ListJobsResponse, Error> {
	return useQuery({
		queryKey: awsS3DataExportIntegrationQueryKeys.jobs(status),
		queryFn: () => listJobs(status),
	});
}

export function useCreateJob(): UseMutationResult<
	{ job: JobRow },
	Error,
	CreateJobBody
> {
	const qc = useQueryClient();
	return useMutation({
		mutationFn: createJob,
		onSuccess: () => {
			qc.invalidateQueries({
				queryKey: awsS3DataExportIntegrationQueryKeys.all,
			});
		},
	});
}
